/**
 * ProposalCard — surfaces a pending ReplanProposal with confirm/reject CTA.
 *
 * Security (CLAUDE.md §4): all strings via React text nodes — no dangerouslySetInnerHTML.
 *
 * Accessibility (CLAUDE.md §7, spec tracking-dashboard):
 *   - role="dialog" with aria-modal and aria-labelledby for the confirmation dialog
 *   - Focus trapped inside dialog on open; focus restored on close
 *   - No window.confirm / native browser dialogs
 *   - aria-live="polite" for success/rejection announcements
 *   - Both action buttons >= 44×44 px (min-h-[44px])
 *   - Keyboard-dismissible (Escape closes dialog)
 *   - Proposal clearly labeled as "proposed" — not applied
 *
 * Propose-not-apply invariant: this component NEVER says the plan was applied.
 * It always says "propuesta" and requires explicit confirmation.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReplanProposal } from "@autodieta/shared";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProposalCardProps {
  /** The pending proposal. When undefined, renders nothing. */
  proposal?: ReplanProposal;
  /** Called with proposalId when human confirms via dialog. */
  onConfirm: (proposalId: string) => Promise<void> | void;
  /** Called with proposalId when human rejects. */
  onReject: (proposalId: string) => Promise<void> | void;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProposalCard({ proposal, onConfirm, onReject }: ProposalCardProps): JSX.Element | null {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const openDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!proposal) return;
    setDialogOpen(false);
    await onConfirm(proposal.id);
    setAnnouncement("El ajuste fue confirmado exitosamente.");
  }, [proposal, onConfirm]);

  const handleReject = useCallback(async () => {
    if (!proposal) return;
    await onReject(proposal.id);
    setAnnouncement("La propuesta fue rechazada.");
  }, [proposal, onReject]);

  if (!proposal) return null;

  const reasonLabel = proposal.reason === "deviation" ? "Desviación del plan" : "Meseta de progreso";

  return (
    <div className="rounded-xl border border-amber-800/60 bg-amber-950/10 p-4">
      {/* Proposal header */}
      <div className="mb-3 flex items-start gap-2">
        <span
          className="inline-block rounded-full bg-amber-900/40 px-2 py-0.5 text-xs font-semibold text-amber-300"
          aria-label="Estado: propuesta pendiente de confirmación"
        >
          Propuesta pendiente
        </span>
      </div>

      {/* Proposal details */}
      <div className="mb-4">
        <p className="text-sm font-medium text-zinc-200">
          Nueva propuesta de ajuste nutricional
        </p>
        <p className="mt-1 text-sm text-zinc-400">
          <span className="font-medium text-zinc-300">Motivo:</span> {reasonLabel}
        </p>
        <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-zinc-500">Energía objetivo</dt>
            <dd className="font-medium text-zinc-100">
              {proposal.targets.targetKcal}{" "}
              <span className="text-xs font-normal text-zinc-400">kcal</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-zinc-500">Proteínas</dt>
            <dd className="font-medium text-zinc-100">
              {proposal.targets.macros.proteinG}{" "}
              <span className="text-xs font-normal text-zinc-400">g</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-zinc-500">Grasas</dt>
            <dd className="font-medium text-zinc-100">
              {proposal.targets.macros.fatG}{" "}
              <span className="text-xs font-normal text-zinc-400">g</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-zinc-500">Carbohidratos</dt>
            <dd className="font-medium text-zinc-100">
              {proposal.targets.macros.carbG}{" "}
              <span className="text-xs font-normal text-zinc-400">g</span>
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-xs text-zinc-500">
          Este es un ajuste propuesto — no se aplica automáticamente. Tu confirmación es
          necesaria antes de cualquier cambio al plan activo.
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={openDialog}
          aria-label="Confirmar ajuste propuesto"
          className="min-h-[44px] flex-1 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
        >
          Confirmar ajuste
        </button>
        <button
          type="button"
          onClick={() => void handleReject()}
          aria-label="Rechazar propuesta de ajuste"
          className="min-h-[44px] flex-1 rounded-lg border border-zinc-600 bg-zinc-800 px-4 py-2 text-sm font-medium text-zinc-200 transition-colors hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
        >
          Rechazar
        </button>
      </div>

      {/* aria-live region for announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      {/* Focus-trapped confirmation dialog */}
      {dialogOpen ? (
        <ConfirmDialog
          targetKcal={proposal.targets.targetKcal}
          onConfirm={() => void handleConfirm()}
          onCancel={closeDialog}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmDialog — focus-trapped, keyboard-dismissible
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  targetKcal: number;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ targetKcal, onConfirm, onCancel }: ConfirmDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const DIALOG_LABEL_ID = "confirm-dialog-title";

  // Focus the first focusable element on mount
  useEffect(() => {
    firstFocusableRef.current?.focus();
  }, []);

  // Trap focus inside dialog + Escape to close
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        onCancel();
        return;
      }

      if (e.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled"));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    },
    [onCancel],
  );

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={DIALOG_LABEL_ID}
        onKeyDown={handleKeyDown}
        className="w-full max-w-sm rounded-2xl border border-zinc-700 bg-zinc-900 p-6 shadow-xl"
      >
        <h3
          id={DIALOG_LABEL_ID}
          className="mb-3 text-base font-semibold text-zinc-100"
        >
          Confirmar ajuste nutricional
        </h3>
        <p className="mb-5 text-sm text-zinc-400">
          ¿Confirmás que querés aplicar el ajuste propuesto con una energía objetivo de{" "}
          <strong className="text-zinc-200">{targetKcal} kcal</strong>?
          Esta acción reemplazará tu plan activo.
        </p>

        <div className="flex gap-3">
          <button
            ref={firstFocusableRef}
            type="button"
            onClick={onConfirm}
            aria-label="Confirmar y aplicar el ajuste"
            className="min-h-[44px] flex-1 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
          >
            Sí, confirmar
          </button>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancelar y volver"
            className="min-h-[44px] flex-1 rounded-lg border border-zinc-600 bg-zinc-800 px-4 py-2 text-sm font-medium text-zinc-200 transition-colors hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
